import { Lock, LockOpen } from "lucide-react";
import { useTokenData, useUnlock } from "@/context/TokenDataContext";
import { AWAITING_LAUNCH } from "@/config/tokenData";
import { FadeIn } from "@/components/motion/Reveal";
import { EnergyIdentity } from "@/components/energy/EnergyIdentity";
import { EnergyChannel } from "@/components/energy/EnergyChannel";
import { LaunchDestination } from "@/components/energy/LaunchDestination";
import { LaunchRocket } from "@/components/energy/LaunchRocket";
import { useEnergyMotion } from "@/components/energy/useEnergyMotion";
import "@/styles/energy-progress.css";
import "@/styles/moon-launch.css";

export const UnlockTicker = ({ className = "" }) => {
  const { isUnlocked, isPreLaunch, marketCap, unlockMarketCap, progress, unlockLabel } = useUnlock();
  const { isSimulated, isLoading, isError } = useTokenData();
  const known = Number.isFinite(marketCap) && marketCap >= 0;
  const safeProgress = known ? Math.max(0, Math.min(progress, 1)) : 0;
  const feed = isSimulated ? "SIMULATION" : isError ? "DATA UNAVAILABLE" : isLoading ? "LOADING DATA" : isPreLaunch ? AWAITING_LAUNCH : "LIVE FEED";
  const motionState = useEnergyMotion({ progress: safeProgress, known, isUnlocked, isSimulated, target: unlockMarketCap });
  const StatusIcon = isUnlocked ? LockOpen : Lock;

  return (
    <FadeIn delay={1.7} className={className}>
      <section ref={motionState.ref} className="kb-energy kb-launch" aria-labelledby="launch-system-title"
        data-testid="hero-unlock-ticker" data-state={isUnlocked ? "unlocked" : "locked"}
        data-prelaunch={isPreLaunch} data-motion={motionState.running ? "running" : "paused"}
        data-celebrating={motionState.celebrating} data-celebration-count={motionState.celebrationCount}>
        <span className="energy-bezel" aria-hidden="true" data-testid="energy-core-frame"
          style={{ borderImageSource: "url(/assets/artwork/energy-progress-frame.webp)" }} />
        <div className="launch-banner" id="launch-system-title" data-testid="launch-system-title">
          <span>KINGBULLISH <span aria-hidden="true">—</span> BEYOND THE MOON</span>
          <span className="launch-banner__system">LAUNCH SYSTEM</span>
        </div>
        <div className="energy-layout" data-testid="hero-energy-progress-core">
          <EnergyIdentity targetLabel={unlockLabel} isUnlocked={isUnlocked} />
          <div className="launch-navigation" data-testid="launch-navigation">
            <EnergyChannel marketCap={marketCap} target={unlockMarketCap} progress={safeProgress} known={known}
              displayed={motionState.displayed} leadingX={motionState.leadingX} feed={feed} rocket />
            <LaunchDestination targetLabel={unlockLabel} isUnlocked={isUnlocked} />
          </div>
          <div className="energy-status" role="status" aria-live="polite" data-testid="energy-core-status-badge">
            <StatusIcon aria-hidden="true" />
            <span data-testid="energy-core-status-text">{isUnlocked ? "ECOSYSTEM UNLOCKED" : "LOCKED"}</span>
            {isUnlocked && <span className="launch-next" data-testid="launch-next-destination">NEXT DESTINATION: BEYOND THE MOON</span>}
          </div>
        </div>
        <LaunchRocket rootRef={motionState.ref} motionState={motionState} progress={safeProgress} known={known} isUnlocked={isUnlocked} />
        {motionState.celebrating && <div className="energy-unlock" aria-hidden="true" data-testid="energy-core-unlock-animation">
          <span className="energy-unlock__wave" />
          {[0, 1, 2, 3, 4, 5].map((i) => <i key={i} className="energy-unlock__spark" style={{ "--spark": i }} />)}
        </div>}
      </section>
    </FadeIn>
  );
};